/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useMemo } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createHook } from '@atlassian/react-sweet-state';
import type {
	RouterContext,
	RouteResource,
	RouteResourceResponse,
} from '../../../common/types.tsx';
import { RouterStore, useRouterStoreActions } from '../../../controllers/router-store/index.tsx';
import type {
	EntireRouterState,
	AllRouterActions,
} from '../../../controllers/router-store/types.tsx';
import type { RouteResourceUpdater, UseResourceHookResponse } from '../../common/types.tsx';

import { useResourceStore, useResourceStoreActions } from '../resource-store/index.tsx';
import { defaultConfiguration } from '../../../defaultConfiguration.tsx';

type UseResourceOptions = {
	routerContext?: RouterContext;
};

export const useResourceBase = <RouteResourceData,>(
	resource: RouteResource<RouteResourceData>,
	options?: UseResourceOptions,
): UseResourceHookResponse<RouteResourceData> => {
	const actions = useResourceStoreActions();
	const { getContext: getRouterContext } = useRouterStoreActions();

	// Dynamically generate a router subscriber based on the resource:
	// makes the component re-render only when key changes instead of
	// after every route change
	const useKey = useMemo(
		() =>
			createHook<EntireRouterState, AllRouterActions, string, RouterContext | undefined>(
				RouterStore,
				{
					// oxlint-disable-next-line no-explicit-any
					selector: (
						{ match, query, route }: { match: any; query: any; route: any },
						keyArg: any,
					): string =>
						resource.getKey(
							keyArg != null ? keyArg : { match, query, route },
							actions.getContext(),
						),
				},
			),
		[resource, actions],
	);

	// oxlint-disable-next-line typescript/no-non-null-asserted-optional-chain, typescript/no-non-null-assertion
	const key = useKey(options?.routerContext!)[0];
	const [slice] = useResourceStore({
		type: resource.type,
		key,
	}) as RouteResourceResponse<RouteResourceData>[];

	// we keep route context bound to key, so route context won't refresh
	// unless key changes. This allows refresh to be called on effect cleanup
	// or asynchronously, when route context might already have changed
	const routerContext = useMemo(
		// oxlint-disable-next-line typescript/no-non-null-asserted-optional-chain, typescript/no-non-null-assertion
		() => options?.routerContext! || getRouterContext(),

		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[key],
	);

	const update = useCallback(
		(updater: RouteResourceUpdater<RouteResourceData>) => {
			actions.updateResourceState(
				resource as RouteResource<unknown>,
				routerContext,
				updater as RouteResourceUpdater<unknown>,
			);
		},
		[resource, routerContext, actions],
	);

	const clear = useCallback(() => {
		actions.clearResource(resource, routerContext);
	}, [resource, routerContext, actions]);

	const clearAll = useCallback(() => {
		actions.clearResource(resource);
	}, [resource, actions]);

	const refresh = useCallback(() => {
		actions.getResourceFromRemote(resource as RouteResource<unknown>, routerContext, {
			prefetch: false,
		});
	}, [resource, routerContext, actions]);

	return useMemo(
		() => ({ ...slice, update, key, refresh, clear, clearAll }),
		[clear, clearAll, key, refresh, slice, update],
	);
};

/**
 * Visible for testing only. Call {@link useResource}.
 * Throws on loading to trigger suspense, to support render-while-fetching on the server.
 */
export const useServerSuspenseResource = <RouteResourceData,>(
	resource: RouteResource<RouteResourceData>,
	options?: UseResourceOptions,
): UseResourceHookResponse<RouteResourceData> => {
	const state = useResourceBase(resource, options);
	const { loading, promise } = state;
	if (loading && promise != null) {
		throw promise;
	}
	return state;
};

export const useResource = <RouteResourceData,>(
	resource: RouteResource<RouteResourceData>,
	options?: UseResourceOptions,
): UseResourceHookResponse<RouteResourceData> => {
	return defaultConfiguration.streaming
		? // oxlint-disable-next-line react-hooks/rules-of-hooks
			useServerSuspenseResource(resource, options)
		: // oxlint-disable-next-line react-hooks/rules-of-hooks
			useResourceBase(resource, options);
};
